import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import client from '../api/client';
import { X, Plus, Trash2, Edit2, Key, Globe, Link as LinkIcon, Server, Shield, Calendar, Save, ExternalLink, Settings, Layout, GripVertical, AlertCircle } from 'lucide-react';

const ClientAssetsModal = ({ isOpen, onClose, client_id, client_name }) => {
    const queryClient = useQueryClient();
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingAsset, setEditingAsset] = useState(null);
    const [formData, setFormData] = useState({
        type: 'access',
        name: '',
        data: { 
            fields: [
                { key: 'Логин', value: '' },
                { key: 'Пароль', value: '', isPassword: true },
                { key: 'URL', value: '' }
            ],
            table: { headers: ['Название', 'Значение'], rows: [['', '']] }
        },
        expiration_date: '',
        notes: ''
    });

    const { data: assets, isLoading } = useQuery({
        queryKey: ['client-assets', client_id],
        queryFn: async () => {
            if (!client_id) return [];
            const res = await client.get(`/client-assets/client/${client_id}`);
            return res.data;
        },
        enabled: !!client_id && isOpen
    });

    const createMutation = useMutation({
        mutationFn: (data) => client.post('/client-assets', { ...data, client_id }),
        onSuccess: () => {
            queryClient.invalidateQueries(['client-assets', client_id]);
            closeForm();
        }
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }) => client.patch(`/client-assets/${id}`, data),
        onSuccess: () => {
            queryClient.invalidateQueries(['client-assets', client_id]);
            closeForm();
        }
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => client.delete(`/client-assets/${id}`),
        onSuccess: () => {
            queryClient.invalidateQueries(['client-assets', client_id]);
        }
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingAsset) {
                await updateMutation.mutateAsync({ id: editingAsset.id, data: formData });
            } else {
                await createMutation.mutateAsync(formData);
            }
        } catch (error) {
            alert("Ошибка при сохранении: " + (error.response?.data?.detail || error.message));
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Удалить этот актив?")) {
            await deleteMutation.mutateAsync(id);
        }
    };

    const closeForm = () => {
        setIsFormOpen(false);
        setEditingAsset(null);
        setFormData({
            type: 'access',
            name: '',
            data: { 
                fields: [
                    { key: 'Логин', value: '' },
                    { key: 'Пароль', value: '', isPassword: true },
                    { key: 'URL', value: '' }
                ],
                table: { headers: ['Название', 'Значение'], rows: [['', '']] }
            },
            expiration_date: '',
            notes: ''
        });
    };

    const openForEdit = (asset) => {
        setEditingAsset(asset);
        
        let processedData = asset.data || {};
        
        // Backward compatibility migration
        if (!processedData.fields && !processedData.table) {
            const fields = [];
            if (processedData.login) fields.push({ key: 'Логин', value: processedData.login });
            if (processedData.password) fields.push({ key: 'Пароль', value: processedData.password, isPassword: true });
            if (processedData.url) fields.push({ key: 'URL', value: processedData.url });
            
            // If data was completely different or empty, provide defaults
            if (fields.length === 0) {
                fields.push({ key: 'Название', value: '' });
            }
            
            processedData = {
                fields,
                table: { headers: ['Название', 'Значение'], rows: [['', '']] }
            };
        }

        setFormData({
            type: asset.type,
            name: asset.name,
            data: processedData,
            expiration_date: asset.expiration_date ? asset.expiration_date.split('T')[0] : '',
            notes: asset.notes || ''
        });
        setIsFormOpen(true);
    };

    const getTypeIcon = (type) => {
        switch (type) {
            case 'access': return <Key className="w-5 h-5 text-amber-500" />;
            case 'domain': return <Globe className="w-5 h-5 text-sky-500" />;
            case 'hosting': return <Server className="w-5 h-5 text-emerald-500" />;
            case 'link': return <LinkIcon className="w-5 h-5 text-pink-500" />;
            case 'table': return <Layout className="w-5 h-5 text-purple-500" />;
            default: return <Shield className="w-5 h-5 text-neutral-500" />;
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-[60] flex items-center justify-center p-4">
            <div className="bg-neutral-900 border border-neutral-800 rounded-[2.5rem] w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl animate-in zoom-in-95 duration-300">
                {/* Header */}
                <div className="p-8 border-b border-neutral-800 flex justify-between items-center bg-neutral-900/50">
                    <div>
                        <h2 className="text-2xl font-black text-white uppercase tracking-tight">Активы и база знаний</h2>
                        <p className="text-[10px] text-blue-500 font-bold uppercase tracking-widest mt-1">Клиент: {client_name}</p>
                    </div>
                    <div className="flex gap-4">
                        {!isFormOpen && (
                            <button
                                onClick={() => setIsFormOpen(true)}
                                className="flex items-center gap-3 bg-blue-600 hover:bg-blue-500 text-white px-6 py-3 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all active:scale-95 shadow-lg shadow-blue-500/20"
                            >
                                <Plus className="w-4 h-4" />
                                Добавить актив
                            </button>
                        )}
                        <button
                            onClick={onClose}
                            className="w-12 h-12 bg-neutral-800 rounded-full flex items-center justify-center text-neutral-400 hover:text-white transition-colors"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-8 custom-scrollbar bg-neutral-950/20">
                    {isFormOpen ? (
                        <div className="max-w-2xl mx-auto py-8">
                            <form onSubmit={handleSubmit} className="space-y-8 animate-in slide-in-from-top-4">
                                <div className="flex items-center gap-4 mb-8">
                                    <div className="w-16 h-16 rounded-3xl bg-blue-500/10 flex items-center justify-center border border-blue-500/20">
                                        {getTypeIcon(formData.type)}
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-black text-white uppercase tracking-tight">
                                            {editingAsset ? 'Редактировать запись' : 'Новая запись'}
                                        </h3>
                                        <p className="text-[10px] text-neutral-500 font-bold uppercase tracking-widest">Заполните данные ассета</p>
                                    </div>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-3">
                                        <label className="block text-[10px] font-black text-neutral-500 uppercase tracking-widest px-2">Тип ресурса</label>
                                        <select
                                            value={formData.type}
                                            onChange={e => setFormData({ ...formData, type: e.target.value })}
                                            className="w-full bg-neutral-900 text-white rounded-2xl px-6 py-4 border border-neutral-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all font-bold appearance-none cursor-pointer"
                                        >
                                            <option value="access">🔐 Доступ / Аккаунт</option>
                                            <option value="domain">🌐 Домен</option>
                                            <option value="hosting">🖥 Хостинг</option>
                                            <option value="link">🔗 Ссылка / База</option>
                                            <option value="table">📊 Таблица данных</option>
                                            <option value="other">📦 Другое</option>
                                        </select>
                                    </div>
                                    <div className="space-y-3">
                                        <label className="block text-[10px] font-black text-neutral-500 uppercase tracking-widest px-2">Название</label>
                                        <input
                                            required
                                            type="text"
                                            value={formData.name}
                                            onChange={e => setFormData({ ...formData, name: e.target.value })}
                                            className="w-full bg-neutral-900 text-white rounded-2xl px-6 py-4 border border-neutral-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all placeholder:text-neutral-700 font-bold"
                                            placeholder="Напр. Админка Сайта"
                                        />
                                    </div>
                                </div>

                                <div className="bg-neutral-900/50 p-8 rounded-[2rem] border border-neutral-800 space-y-8">
                                    {formData.type === 'table' ? (
                                        /* Table Editor */
                                        <div className="space-y-6">
                                            <div className="flex justify-between items-center mb-4">
                                                <h4 className="text-[10px] font-black text-neutral-500 uppercase tracking-widest px-2">Редактор таблицы</h4>
                                                <button 
                                                    type="button"
                                                    onClick={() => {
                                                        const newHeaders = [...formData.data.table.headers, `Колонка ${formData.data.table.headers.length + 1}`];
                                                        const newRows = formData.data.table.rows.map(r => [...r, '']);
                                                        setFormData({
                                                            ...formData,
                                                            data: { 
                                                                ...formData.data, 
                                                                table: { headers: newHeaders, rows: newRows } 
                                                            }
                                                        });
                                                    }}
                                                    className="text-[9px] font-black text-blue-500 uppercase tracking-widest hover:text-blue-400 transition-colors"
                                                >
                                                    + Добавить колонку
                                                </button>
                                            </div>

                                            <div className="overflow-x-auto custom-scrollbar pb-4">
                                                <table className="w-full border-collapse">
                                                    <thead>
                                                        <tr>
                                                            {formData.data.table.headers.map((h, i) => (
                                                                <th key={i} className="p-2 min-w-[150px]">
                                                                    <div className="flex gap-2">
                                                                        <input 
                                                                            type="text"
                                                                            value={h}
                                                                            onChange={(e) => {
                                                                                const newHeaders = [...formData.data.table.headers];
                                                                                newHeaders[i] = e.target.value;
                                                                                setFormData({
                                                                                    ...formData,
                                                                                    data: { ...formData.data, table: { ...formData.data.table, headers: newHeaders } }
                                                                                });
                                                                            }}
                                                                            className="w-full bg-neutral-800 text-white rounded-xl px-3 py-2 text-[10px] font-black uppercase tracking-widest border border-neutral-700 outline-none focus:border-blue-500"
                                                                        />
                                                                        {formData.data.table.headers.length > 1 && (
                                                                            <button 
                                                                                type="button"
                                                                                onClick={() => {
                                                                                    const newHeaders = formData.data.table.headers.filter((_, idx) => idx !== i);
                                                                                    const newRows = formData.data.table.rows.map(r => r.filter((_, idx) => idx !== i));
                                                                                    setFormData({
                                                                                        ...formData,
                                                                                        data: { ...formData.data, table: { headers: newHeaders, rows: newRows } }
                                                                                    });
                                                                                }}
                                                                                className="text-neutral-600 hover:text-rose-500"
                                                                            >
                                                                                <X className="w-3 h-3" />
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </th>
                                                            ))}
                                                            <th className="w-10"></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {formData.data.table.rows.map((row, rowIndex) => (
                                                            <tr key={rowIndex} className="border-t border-neutral-800">
                                                                {row.map((cell, cellIndex) => (
                                                                    <td key={cellIndex} className="p-2">
                                                                        <input 
                                                                            type="text"
                                                                            value={cell}
                                                                            onChange={(e) => {
                                                                                const newRows = [...formData.data.table.rows];
                                                                                newRows[rowIndex][cellIndex] = e.target.value;
                                                                                setFormData({
                                                                                    ...formData,
                                                                                    data: { ...formData.data, table: { ...formData.data.table, rows: newRows } }
                                                                                });
                                                                            }}
                                                                            className="w-full bg-neutral-950 text-white rounded-xl px-3 py-2 text-xs border border-transparent focus:border-neutral-700 outline-none"
                                                                        />
                                                                    </td>
                                                                ))}
                                                                <td className="p-2">
                                                                    <button 
                                                                        type="button"
                                                                        onClick={() => {
                                                                            const newRows = formData.data.table.rows.filter((_, idx) => idx !== rowIndex);
                                                                            setFormData({
                                                                                ...formData,
                                                                                data: { ...formData.data, table: { ...formData.data.table, rows: newRows } }
                                                                            });
                                                                        }}
                                                                        className="text-neutral-700 hover:text-rose-500"
                                                                    >
                                                                        <Trash2 className="w-4 h-4" />
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                            <button 
                                                type="button"
                                                onClick={() => {
                                                    const newRows = [...formData.data.table.rows, Array(formData.data.table.headers.length).fill('')];
                                                    setFormData({
                                                        ...formData,
                                                        data: { 
                                                            ...formData.data, 
                                                            table: { ...formData.data.table, rows: newRows } 
                                                        }
                                                    });
                                                }}
                                                className="w-full py-3 bg-neutral-950 border border-dashed border-neutral-800 rounded-2xl text-[10px] font-black uppercase tracking-widest text-neutral-500 hover:text-white hover:border-blue-500/50 transition-all flex items-center justify-center gap-2"
                                            >
                                                <Plus className="w-3 h-3" />
                                                Добавить строку
                                            </button>
                                        </div>
                                    ) : (
                                        /* Dynamic Fields Editor */
                                        <div className="space-y-6">
                                            <div className="flex justify-between items-center mb-2">
                                                <h4 className="text-[10px] font-black text-neutral-500 uppercase tracking-widest px-2">Произвольные поля</h4>
                                                <button 
                                                    type="button"
                                                    onClick={() => {
                                                        const newFields = [...formData.data.fields, { key: '', value: '' }];
                                                        setFormData({ ...formData, data: { ...formData.data, fields: newFields } });
                                                    }}
                                                    className="text-[9px] font-black text-blue-500 uppercase tracking-widest hover:text-blue-400 transition-colors"
                                                >
                                                    + Добавить поле
                                                </button>
                                            </div>
                                            
                                            <div className="space-y-4">
                                                {formData.data.fields.map((field, index) => (
                                                    <div key={index} className="grid grid-cols-12 gap-3 items-end animate-in fade-in slide-in-from-left-2 duration-300">
                                                        <div className="col-span-4 space-y-2">
                                                            <label className="text-[9px] font-black text-neutral-700 uppercase tracking-widest px-1">Метка</label>
                                                            <input 
                                                                type="text"
                                                                value={field.key}
                                                                onChange={(e) => {
                                                                    const newFields = [...formData.data.fields];
                                                                    newFields[index].key = e.target.value;
                                                                    setFormData({ ...formData, data: { ...formData.data, fields: newFields } });
                                                                }}
                                                                className="w-full bg-neutral-950 text-neutral-400 rounded-xl px-4 py-3 border border-neutral-800 focus:border-neutral-700 outline-none text-[10px] font-black uppercase tracking-widest"
                                                                placeholder="Напр. Логин"
                                                            />
                                                        </div>
                                                        <div className="col-span-7 space-y-2">
                                                            <div className="flex justify-between px-1">
                                                                <label className="text-[9px] font-black text-neutral-700 uppercase tracking-widest">Значение</label>
                                                                <button 
                                                                    type="button"
                                                                    onClick={() => {
                                                                        const newFields = [...formData.data.fields];
                                                                        newFields[index].isPassword = !newFields[index].isPassword;
                                                                        setFormData({ ...formData, data: { ...formData.data, fields: newFields } });
                                                                    }}
                                                                    className={`text-[8px] font-black uppercase tracking-[0.2em] transition-colors ${field.isPassword ? 'text-amber-500' : 'text-neutral-800 hover:text-neutral-500'}`}
                                                                >
                                                                    {field.isPassword ? '👁 Скрыть' : '🔒 Маскировать'}
                                                                </button>
                                                            </div>
                                                            <input 
                                                                type={field.isPassword ? "password" : "text"}
                                                                value={field.value}
                                                                onChange={(e) => {
                                                                    const newFields = [...formData.data.fields];
                                                                    newFields[index].value = e.target.value;
                                                                    setFormData({ ...formData, data: { ...formData.data, fields: newFields } });
                                                                }}
                                                                className="w-full bg-neutral-950 text-white rounded-xl px-4 py-3 border border-neutral-800 focus:border-blue-500 outline-none text-xs font-medium transition-all"
                                                                placeholder="..."
                                                            />
                                                        </div>
                                                        <div className="col-span-1 pb-1 flex justify-center">
                                                            <button 
                                                                type="button"
                                                                onClick={() => {
                                                                    const newFields = formData.data.fields.filter((_, i) => i !== index);
                                                                    setFormData({ ...formData, data: { ...formData.data, fields: newFields } });
                                                                }}
                                                                className="w-8 h-8 rounded-lg bg-neutral-950 flex items-center justify-center text-neutral-700 hover:text-rose-500 hover:bg-rose-500/10 transition-all mb-1"
                                                            >
                                                                <Trash2 className="w-3.5 h-3.5" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-3">
                                        <label className="block text-[10px] font-black text-neutral-500 uppercase tracking-widest px-2 group flex items-center gap-2">
                                            <Calendar className="w-3 h-3 text-blue-500" />
                                            Дата истечения
                                        </label>
                                        <input
                                            type="date"
                                            value={formData.expiration_date}
                                            onChange={e => setFormData({ ...formData, expiration_date: e.target.value })}
                                            className="w-full bg-neutral-900 text-white rounded-2xl px-6 py-4 border border-neutral-800 focus:border-blue-500 outline-none selection:bg-blue-500/30"
                                        />
                                        <p className="text-[9px] text-neutral-600 px-2 italic font-medium">* Система напомнит за 7 дней до этой даты</p>
                                    </div>
                                    <div className="space-y-3">
                                        <label className="block text-[10px] font-black text-neutral-500 uppercase tracking-widest px-2">Заметки</label>
                                        <textarea
                                            value={formData.notes}
                                            onChange={e => setFormData({ ...formData, notes: e.target.value })}
                                            className="w-full bg-neutral-900 text-white rounded-2xl px-6 py-4 border border-neutral-800 focus:border-blue-500 outline-none min-h-[58px] resize-none"
                                            placeholder="Любая важная информация..."
                                        />
                                    </div>
                                </div>

                                <div className="flex gap-4 pt-8">
                                    <button
                                        type="submit"
                                        disabled={createMutation.isLoading || updateMutation.isLoading}
                                        className="flex-1 bg-blue-600 hover:bg-blue-500 text-white py-5 rounded-2xl font-black uppercase text-xs tracking-widest transition-all flex items-center justify-center gap-3 shadow-xl shadow-blue-500/10 active:scale-95 disabled:opacity-50"
                                    >
                                        <Save className="w-5 h-5" />
                                        Сохранить актив
                                    </button>
                                    <button
                                        type="button"
                                        onClick={closeForm}
                                        className="px-10 bg-neutral-800 hover:bg-neutral-700 text-white py-5 rounded-2xl font-black uppercase text-xs tracking-widest transition-all active:scale-95"
                                    >
                                        Отмена
                                    </button>
                                </div>
                            </form>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {isLoading ? (
                                <div className="flex flex-col items-center justify-center py-32 space-y-4">
                                    <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
                                    <p className="text-[10px] font-black text-neutral-500 uppercase tracking-[0.2em]">Загрузка базы знаний...</p>
                                </div>
                            ) : assets?.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-32 border-2 border-dashed border-neutral-800/50 rounded-[3rem] bg-neutral-950/20 group hover:border-blue-500/20 transition-all cursor-pointer" onClick={() => setIsFormOpen(true)}>
                                    <div className="w-20 h-20 rounded-full bg-neutral-900 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                                        <Shield className="w-8 h-8 text-neutral-700 group-hover:text-blue-500/50 transition-colors" />
                                    </div>
                                    <h3 className="text-white font-black uppercase tracking-widest text-sm mb-2">База знаний пуста</h3>
                                    <p className="text-xs text-neutral-600 text-center max-w-[280px] font-medium">Храните здесь пароли, ссылки, информацию о доменах и хостинге клиента.</p>
                                    <button className="mt-8 text-blue-500 text-[10px] font-black uppercase tracking-widest hover:text-blue-400 transition-colors">Создать первую запись</button>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-20">
                                    {assets.map(asset => (
                                        <div key={asset.id} className="bg-neutral-900/40 border border-neutral-800/60 p-8 rounded-[2.5rem] hover:border-blue-500/40 hover:bg-neutral-900/60 transition-all group relative overflow-hidden backdrop-blur-sm shadow-xl shadow-black/20">
                                            {/* Accent glow */}
                                            <div className="absolute -right-4 -top-4 w-24 h-24 bg-blue-500/5 blur-2xl group-hover:bg-blue-500/10 transition-all rounded-full" />
                                            
                                            <div className="flex justify-between items-start mb-6 relative">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-14 h-14 rounded-2xl bg-neutral-950 border border-neutral-800 flex items-center justify-center shadow-inner group-hover:scale-110 transition-transform duration-500">
                                                        {getTypeIcon(asset.type)}
                                                    </div>
                                                    <div>
                                                        <h4 className="text-white font-black text-lg tracking-tight leading-tight">{asset.name}</h4>
                                                        <span className="text-[9px] text-neutral-500 border border-neutral-800 px-2 py-0.5 rounded-lg uppercase font-black tracking-widest mt-2 inline-block">{asset.type}</span>
                                                    </div>
                                                </div>
                                                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-all translate-x-4 group-hover:translate-x-0">
                                                    <button onClick={() => openForEdit(asset)} className="w-10 h-10 flex items-center justify-center bg-white/5 hover:bg-white/10 text-neutral-400 hover:text-white rounded-xl transition-all">
                                                        <Edit2 className="w-4 h-4" />
                                                    </button>
                                                    <button onClick={() => handleDelete(asset.id)} className="w-10 h-10 flex items-center justify-center bg-rose-500/10 hover:bg-rose-500 text-rose-500 hover:text-white rounded-xl transition-all">
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="space-y-4 relative">
                                                {asset.data.url && (
                                                    <div className="bg-neutral-950/40 p-3 rounded-2xl border border-neutral-800/50">
                                                        <a href={asset.data.url} target="_blank" rel="noreferrer" className="text-[11px] text-blue-400 hover:text-blue-300 transition-colors flex items-center gap-2 font-bold truncate">
                                                            <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
                                                            {asset.data.url.replace(/^https?:\/\//, '')}
                                                        </a>
                                                    </div>
                                                )}
                                                
                                                {/* Dynamic Fields Display */}
                                                {asset.data?.fields && asset.data.fields.length > 0 && (
                                                    <div className="space-y-2 px-1">
                                                        {asset.data.fields.map((f, i) => (
                                                            f.value && (
                                                                <div key={i} className="flex justify-between text-xs items-center gap-4">
                                                                    <span className="text-[9px] text-neutral-600 uppercase font-black tracking-tighter whitespace-nowrap">{f.key || 'Field'}:</span>
                                                                    {f.isPassword ? (
                                                                        <span className="bg-neutral-800 hover:bg-neutral-700 text-white px-2 py-1 rounded-lg font-mono blur-[4px] hover:blur-none transition-all cursor-help text-[10px] border border-neutral-700/50 truncate">
                                                                            {f.value}
                                                                        </span>
                                                                    ) : (
                                                                        <span className="text-neutral-300 font-bold truncate text-right">
                                                                            {f.key?.toLowerCase() === 'url' ? (
                                                                                <a href={f.value.startsWith('http') ? f.value : `https://${f.value}`} target="_blank" rel="noreferrer" className="text-blue-400 hover:underline">
                                                                                    {f.value.replace(/^https?:\/\//, '')}
                                                                                </a>
                                                                            ) : f.value}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            )
                                                        ))}
                                                    </div>
                                                )}

                                                {/* Table Display */}
                                                {asset.type === 'table' && asset.data?.table && (
                                                    <div className="mt-4 bg-neutral-950/40 rounded-2xl border border-neutral-800/50 overflow-hidden">
                                                        <div className="overflow-x-auto custom-scrollbar">
                                                            <table className="w-full text-[10px] text-left">
                                                                <thead>
                                                                    <tr className="bg-neutral-900/50">
                                                                        {asset.data.table.headers.map((h, i) => (
                                                                            <th key={i} className="px-3 py-2 font-black uppercase tracking-tight text-neutral-500 border-b border-neutral-800">{h}</th>
                                                                        ))}
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {asset.data.table.rows.map((row, i) => (
                                                                        <tr key={i} className="hover:bg-white/5 transition-colors">
                                                                            {row.map((cell, j) => (
                                                                                <td key={j} className="px-3 py-2 text-neutral-400 border-b border-neutral-800/30 font-medium">{cell}</td>
                                                                            ))}
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                )}

                                                {asset.notes && (
                                                    <div className="text-[10px] text-neutral-500 bg-neutral-950/30 p-4 rounded-2xl border border-neutral-900 font-medium leading-relaxed italic">
                                                        {asset.notes}
                                                    </div>
                                                )}

                                                {asset.expiration_date && (
                                                    <div className={`mt-2 pt-4 border-t border-neutral-800/50 flex items-center justify-between`}>
                                                        <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-amber-500/80">
                                                            <Calendar className="w-3.5 h-3.5" />
                                                            Истекает
                                                        </div>
                                                        <div className="text-xs font-black text-neutral-200 bg-neutral-800 px-3 py-1 rounded-xl">
                                                            {new Date(asset.expiration_date).toLocaleDateString()}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ClientAssetsModal;
